import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip
} from '@mui/material';
import releaseNotes from '../data/releaseNotes.json';

interface ReleaseEntry {
  version: string;
  date: string;
  title: string;
  highlights: string[];
}

const LAST_SEEN_VERSION_KEY = 'qr_redirect_last_seen_version';

export function getLatestVersion(): string {
  return releaseNotes[0]?.version || '1.0.0';
}

export function hasUnseenRelease(): boolean {
  const lastSeen = localStorage.getItem(LAST_SEEN_VERSION_KEY);
  return lastSeen !== getLatestVersion();
}

export function markReleaseAsSeen(): void {
  localStorage.setItem(LAST_SEEN_VERSION_KEY, getLatestVersion());
}

const ReleaseNotes: React.FC = () => {
  useEffect(() => {
    markReleaseAsSeen();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isLatest = (index: number) => index === 0;

  return (
    <Box
      sx={{
        flex: 1,
        p: 4,
        background: '#fff',
        overflow: 'auto',
      }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000' }}>
            Release Notes
          </Typography>
        </Box>

        <Typography
          variant="body1"
          sx={{
            color: '#1a1a1a',
            mb: 4,
            fontSize: '17px',
          }}
        >
          Stay up to date with the latest improvements and features.
        </Typography>

        {(releaseNotes as ReleaseEntry[]).map((release, index) => (
          <Paper
            key={release.version}
            elevation={isLatest(index) ? 3 : 1}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: isLatest(index) ? '2px solid #2196F3' : '1px solid #e0e0e0',
              position: 'relative',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  color: '#1a1a1a',
                }}
              >
                v{release.version}
              </Typography>

              {isLatest(index) && (
                <Chip
                  label="Latest"
                  size="small"
                  sx={{
                    backgroundColor: '#2196F3',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '11px',
                    height: 22
                  }}
                />
              )}

              <Typography
                variant="body2"
                sx={{
                  color: '#000',
                  fontSize: '16px',
                  ml: 'auto',
                }}
              >
                {formatDate(release.date)}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '17px',
                color: '#1a1a1a',
                mb: 1.5,
              }}
            >
              {release.title}
            </Typography>

            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {release.highlights.map((item, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    color: '#1a1a1a',
                    fontSize: '16px',
                    lineHeight: 1.8,
                    '&::marker': {
                      color: '#2196F3'
                    }
                  }}
                >
                  {item}
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
    </Box>
  );
};

export default ReleaseNotes;
